import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, Send, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { RatingBadge } from "@/components/fleet/Stars";
import { VerifiedBadge } from "@/components/fleet/RideCard";

const waLink = (phone) => `https://wa.me/${phone.replace(/[^\d]/g, "").replace(/^(?!39)(3\d{8,9})$/, "39$1")}`;

const Counterpart = ({ c }) => (
  <div data-testid="chat-counterpart" className="ff-card p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <span className="font-bold text-white truncate">{c.role}: {c.name}</span>
      <div className="flex items-center gap-2">{c.verified && <VerifiedBadge testid="chat-counterpart-verified" />}<RatingBadge rating={c.rating} testid="chat-counterpart-rating" /></div>
    </div>
    {c.phone ? (
      <div className="grid grid-cols-2 gap-2">
        <a href={`tel:${c.phone}`} data-testid="chat-call-link" className="ff-btn-secondary !min-h-[48px] text-sm border-[#3B82F6]"><Phone size={16} /> {c.phone}</a>
        <a href={waLink(c.phone)} target="_blank" rel="noopener noreferrer" data-testid="chat-whatsapp-link" className="ff-btn-secondary !min-h-[48px] text-sm border-[#25D366] text-[#4ADE80]"><MessageCircle size={16} /> WhatsApp</a>
      </div>
    ) : <span data-testid="chat-no-phone" className="text-sm text-[#94A3B8]">Il collega non ha ancora inserito il telefono: usa la chat.</span>}
  </div>
);

export const ChatDialog = ({ ride, onClose }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const load = useCallback(() => api.get(`/rides/${ride.id}/messages`).then((r) => setData(r.data)).catch(() => {}), [ride]);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/rides/${ride.id}/messages`, { text });
      setText("");
      load();
    } catch (err) {
      toast.error(apiError(err, "Messaggio non inviato"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="chat-dialog" className="ff-glass-panel sm:max-w-md max-h-[92vh] flex flex-col rounded-2xl p-5">
        <DialogHeader className="text-left">
          <DialogTitle className="ff-display text-xl font-extrabold">Contatti & Chat</DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-sm">{ride.origin} ➔ {ride.destination} • scambiatevi qui i dati del passeggero.</DialogDescription>
        </DialogHeader>
        {data && <Counterpart c={data.counterpart} />}
        <div data-testid="chat-messages" className="flex-1 min-h-[180px] max-h-[40vh] overflow-y-auto flex flex-col gap-2 py-2">
          {data?.messages?.length === 0 && <p className="text-sm text-[#94A3B8] text-center py-6">Nessun messaggio. Scrivi nome passeggero, volo, punto d'incontro…</p>}
          {data?.messages?.map((m) => (
            <div key={m.id} data-testid={`chat-message-${m.id}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.mine ? "self-end bg-[#10B981] text-black" : "self-start bg-[#18181B] text-white"}`}>
              {!m.mine && <span className="block text-[11px] font-bold text-[#94A3B8]">{m.sender_name}</span>}
              <span className="whitespace-pre-wrap break-words">{m.text}</span>
              <span className={`block text-[10px] mt-0.5 ${m.mine ? "text-black/60" : "text-[#64748B]"}`}>{new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2">
          <input data-testid="chat-input" className="ff-field flex-1" maxLength={1000} placeholder="Scrivi un messaggio…" value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" data-testid="chat-send-button" disabled={sending || !text.trim()} aria-label="Invia" className="ff-btn-primary !min-w-[56px] px-4"><Send size={18} /></button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
